import {useCallback, useState} from 'react';
import {ChatRequest, ChatResponse} from '../chatbot/types';
import {useAuth} from './use-auth';
import {createSignedFetcher} from 'aws-sigv4-fetch';

export interface UseChatbotApiResult {
    sendMessage: (
        message: string,
        sessionId?: string,
        context?: ChatRequest['context'],
        onStreamChunk?: (chunk: string) => void
    ) => Promise<string>;
    isLoading: boolean;
    error: Error | null;
}

export function useChatbotApi(): UseChatbotApiResult {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const auth = useAuth();

    const sendMessage = useCallback(
        async (
            message: string,
            sessionId?: string,
            context?: ChatRequest['context'],
            onStreamChunk?: (chunk: string) => void
        ): Promise<string> => {
            // Reset error state at the start of each request
            setError(null);
            setIsLoading(true);

            try {
                // Construct the request payload for the Lambda function
                const requestPayload = {
                    message: message,
                    sessionId: sessionId,
                };

                // Lambda function URL endpoint
                const apiEndpoint = 'https://fj4kl4yxk2tsjowpfuysbgulge0twxxq.lambda-url.us-west-2.on.aws';

                // Get AWS credentials from auth context
                if (!auth.authInformation) {
                    throw new Error('User is not authenticated');
                }

                // Get credentials for signing the request
                const endpoints = auth.authInformation.getEndpoints();
                const credentials = endpoints['NA']?.['ExplainabilityView']?.credentials;

                if (!credentials) {
                    throw new Error('AWS credentials not available');
                }

                // Create signed fetcher with AWS credentials
                const signedFetch = createSignedFetcher({
                    service: 'lambda',
                    region: 'us-west-2',
                    credentials: {
                        accessKeyId: credentials.accessKeyId,
                        secretAccessKey: credentials.secretAccessKey,
                        sessionToken: credentials.sessionToken,
                    },
                });

                // Make the signed API call
                const response = await signedFetch(apiEndpoint, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Stream-Response': 'true',
                    },
                    body: JSON.stringify(requestPayload),
                });

                // Handle non-OK responses
                if (!response.ok) {
                    // Try to get error details from response body
                    let errorMessage = `API request failed with status ${response.status}`;
                    try {
                        const errorData = await response.json();
                        if (errorData.error) {
                            errorMessage = JSON.stringify(errorData.error);
                        } else if (errorData.message) {
                            errorMessage = JSON.stringify(errorData.message);
                        }
                    } catch {
                        // If we can't parse the error response, use the default message
                    }

                    // Handle different error status codes
                    if (response.status === 401 || response.status === 403) {
                        throw new Error(`Authentication/Authorization error: ${errorMessage}`);
                    } else if (response.status === 408 || response.status === 504) {
                        throw new Error('Request timed out. Please try again.');
                    } else if (response.status >= 500) {
                        throw new Error(`Server error: ${errorMessage}`);
                    } else {
                        throw new Error(errorMessage);
                    }
                }

                // Handle streaming response if callback is provided
                if (onStreamChunk && response.body) {
                    console.log('Response headers:', Object.fromEntries(response.headers.entries()));
                    console.log('Starting to read stream...');

                    const reader = response.body.getReader();
                    const decoder = new TextDecoder();
                    let fullResponse = '';
                    let chunkCount = 0;

                    try {
                        // eslint-disable-next-line no-constant-condition
                        while (true) {
                            const {done, value} = await reader.read();

                            if (done) {
                                console.log(`Stream complete. Total chunks: ${chunkCount}, Full length: ${fullResponse.length}`);
                                break;
                            }

                            chunkCount++;
                            // Decode the chunk
                            const chunk = decoder.decode(value, {stream: true});
                            console.log(`Chunk ${chunkCount} received (${value.byteLength} bytes):`, chunk.substring(0, 100));
                            fullResponse += chunk;

                            // Call the streaming callback with the accumulated response
                            onStreamChunk(fullResponse);
                        }

                        console.log('Streaming complete. Full response:', fullResponse);
                        return fullResponse;
                    } catch (streamError) {
                        console.error('Error reading stream:', streamError);
                        throw new Error('Failed to read streaming response');
                    }
                }

                // Fallback to non-streaming response
                const data = await response.json();

                console.log('API response', JSON.stringify(data));

                // Lambda function URL returns a response with statusCode, headers, and body
                // The body is a stringified JSON that contains the actual answer
                if (data.body) {
                    const bodyData = JSON.parse(data.body);

                    // Handle error responses with model_response (e.g., NoAnswerFromModelError)
                    if (bodyData.error?.model_response) {
                        return bodyData.error.model_response;
                    }

                    // Handle error responses with just a message (e.g., NoContextFoundError)
                    if (bodyData.error?.message) {
                        return bodyData.error.message;
                    }

                    if (bodyData.answer) {
                        return bodyData.answer;
                    } else if (bodyData.response) {
                        return bodyData.response;
                    } else if (bodyData.message) {
                        return bodyData.message;
                    }
                }

                // Fallback: handle direct response formats
                if (typeof data === 'string') {
                    return data;
                } else if (data.response) {
                    return data.response;
                } else if (data.message) {
                    return data.message;
                } else if (data.answer) {
                    return data.answer;
                } else {
                    throw new Error('Unexpected response format from chatbot service');
                }
            } catch (err) {
                // Handle network errors
                if (err instanceof TypeError && err.message.includes('fetch')) {
                    const networkError = new Error(
                        'Unable to connect to the chatbot service. Please check your connection and try again.'
                    );
                    setError(networkError);
                    throw networkError;
                }

                // Re-throw the error after setting state
                const error = err instanceof Error ? err : new Error('An unexpected error occurred');
                setError(error);
                throw error;
            } finally {
                // Always reset loading state
                setIsLoading(false);
            }
        },
        [auth]
    );

    return {
        sendMessage,
        isLoading,
        error,
    };
}
